import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Image as ImageIcon } from 'lucide-react';
import { MultiRestaurantSelector } from '@/components/MultiRestaurantSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'COLLECTION', label: 'Collection' },
  { value: 'TRAIN', label: 'Train' },
  { value: 'PARTY', label: 'Party' },
  { value: 'OFFER', label: 'Offer' },
  { value: 'NO_ONION_GARLIC', label: 'Without Onion & Garlic' },
  { value: 'OTHER', label: 'Other' }
];

const typeBadgeColor = (t) => {
  switch (t) {
    case 'COLLECTION': return 'bg-blue-100 text-blue-700';
    case 'TRAIN': return 'bg-amber-100 text-amber-700';
    case 'PARTY': return 'bg-purple-100 text-purple-700';
    case 'OFFER': return 'bg-rose-100 text-rose-700';
    case 'NO_ONION_GARLIC': return 'bg-lime-100 text-lime-800';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const ExploreMore = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const emptyForm = {
    title: '',
    type: 'COLLECTION',
    link: '',
    priority: 0,
    status: true,
    resturantIds: []
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/explore-more`);
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load Explore More items');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setCurrent(null);
    setImageFile(null);
    setPreviewUrl('');
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditMode(true);
    setCurrent(item);
    setImageFile(null);
    setPreviewUrl(item.image || '');
    setFormData({
      title: item.title || '',
      type: item.type || 'COLLECTION',
      link: item.link || '',
      priority: item.priority ?? 0,
      status: item.status ?? true,
      resturantIds: item.resturantIds || []
    });
    setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP, GIF allowed');
      return;
    }
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!editMode && !imageFile) {
      toast.error('Image is required');
      return;
    }

    const payload = new FormData();
    payload.append('title', formData.title.trim());
    payload.append('type', formData.type);
    payload.append('link', formData.link || '');
    payload.append('priority', String(formData.priority));
    payload.append('status', String(formData.status));
    payload.append('resturantIds', JSON.stringify(formData.resturantIds || []));
    if (imageFile) payload.append('image', imageFile);

    try {
      if (editMode && current) {
        await axios.patch(`${API}/explore-more/${current.id}`, payload);
        toast.success('Updated');
      } else {
        await axios.post(`${API}/explore-more`, payload);
        toast.success('Created');
      }
      setShowDialog(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/explore-more/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin h-12 w-12 border-2 border-orange-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="explore-more-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold">Explore More</h1>
          <p className="text-sm text-gray-500 mt-1">
            Home-screen tiles users see — link each to one or more restaurants.
          </p>
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              data-testid="explore-add-btn"
              onClick={openCreate}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="mr-2" size={18} />
              Add Item
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Edit Item' : 'Create Explore More Item'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <Label>Title *</Label>
                <Input
                  data-testid="explore-title-input"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="e.g. Party Packs"
                  required
                />
              </div>

              {/* Image */}
              <div>
                <Label>Image{!editMode && ' *'}</Label>
                <Input
                  data-testid="explore-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  required={!editMode}
                />
                {previewUrl && (
                  <div className="mt-3">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="h-40 w-full object-cover rounded border"
                    />
                  </div>
                )}
              </div>

              {/* Type + Priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(v) => setFormData({ ...formData, type: v })}
                  >
                    <SelectTrigger data-testid="explore-type-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: Math.max(0, Number(e.target.value))
                      })
                    }
                  />
                </div>
              </div>

              {/* Link */}
              <div>
                <Label>Link (optional)</Label>
                <Input
                  value={formData.link}
                  onChange={(e) =>
                    setFormData({ ...formData, link: e.target.value })
                  }
                  placeholder="Optional deeplink or URL"
                />
              </div>

              {/* Restaurants multi-select — not used for the automatic
                  "Without Onion & Garlic" collection */}
              {formData.type === 'NO_ONION_GARLIC' ? (
                <div className="rounded-md border border-lime-200 bg-lime-50 p-3 text-sm text-lime-900">
                  Restaurants are picked automatically: the app shows every restaurant that has
                  at least one available item marked <b>Without Onion &amp; Garlic</b>
                  (Restaurant → Items → edit item, or bulk-mark from the item list).
                </div>
              ) : (
                <MultiRestaurantSelector
                  key={current?.id || 'new'}
                  initialSelected={editMode ? current?.resturants || [] : []}
                  value={formData.resturantIds}
                  onChange={(ids) =>
                    setFormData({ ...formData, resturantIds: ids })
                  }
                  label="Linked Restaurants"
                />
              )}

              {/* Status */}
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status.toString()}
                  onValueChange={(v) =>
                    setFormData({ ...formData, status: v === 'true' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                data-testid="explore-submit-btn"
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {editMode ? 'Update Item' : 'Create Item'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* GRID */}
      {items.length === 0 ? (
        <div className="text-center text-gray-500 py-16 border rounded-lg bg-white">
          <ImageIcon className="mx-auto mb-3 opacity-40" size={40} />
          No Explore More items yet. Click <b>Add Item</b> to create the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`explore-card-${item.id}`}
              className="bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col"
            >
              <div className="h-40 w-full bg-gray-100 relative">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-gray-400">
                    <ImageIcon size={32} />
                  </div>
                )}
                <Badge
                  className={`absolute top-2 left-2 ${typeBadgeColor(item.type)}`}
                >
                  {item.type}
                </Badge>
              </div>

              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold">{item.title}</h3>
                  <Badge
                    className={
                      item.status
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-600'
                    }
                  >
                    {item.status ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                <div className="text-xs text-gray-500">
                  Priority: <span className="font-medium">{item.priority}</span>
                </div>

                <div className="text-xs">
                  {item.resturants && item.resturants.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {item.resturants.slice(0, 3).map((r) => (
                        <Badge
                          key={r.id}
                          variant="secondary"
                          className="text-[10px] font-normal"
                        >
                          {r.name}
                        </Badge>
                      ))}
                      {item.resturants.length > 3 && (
                        <span className="text-gray-500 text-[10px] self-center">
                          +{item.resturants.length - 3} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">No linked restaurants</span>
                  )}
                </div>

                <div className="mt-auto flex gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => openEdit(item)}
                    data-testid={`explore-edit-${item.id}`}
                  >
                    <Edit size={14} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeItem(item.id)}
                    data-testid={`explore-delete-${item.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExploreMore;
